import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { ResetUserPasswordToken } from './compiled_proto/com/celertech/baseserver/api/user/DownstreamAuthenticationUserProto';
import { Button } from './components/common/Button';
import LanguageButton from './components/common/LanguageButton';
import StatusIndicator from './components/common/StatusIndicator';
import { forgotPasswordSchema } from './components/form/schema/forgotPasswordSchema';
import RHFInput from './components/inputs/RHFInput';
import language from './config/language';
import { LoginFooter, LoginLogo } from './Login';
import { logToServer } from './services/LogService';
import { sendResetPasswordEmail } from './services/UserService';
import { useAppSelector } from './state/hooks';
import { selectLanguage } from './state/reducers/languageSlice';
import useToast from './utils/hooks/useToast';

interface ForgotPasswordFormValues {
    username: string;
    server: unknown;
}

function ForgotPassword() {
    const lang = useAppSelector(selectLanguage);

    const navigate = useNavigate();
    const [toastError, toastSuccess] = useToast();

    const [retryTimer, setRetryTimer] = useState<number>(0);
    const [resetPasswordResponse, setResetPasswordResponse] = useState<ResetUserPasswordToken>();
    const form = useForm<ForgotPasswordFormValues>({
        defaultValues: {
            username: ''
        },
        mode: 'onChange',
        resolver: yupResolver(forgotPasswordSchema)
    });
    const {
        handleSubmit,
        setError,
        formState: { errors, isSubmitted, isSubmitting },
        register
    } = form;

    const onSubmit = async (data: ForgotPasswordFormValues) => {
        try {
            const user = await sendResetPasswordEmail(data.username);
            logToServer('info', `User [${data.username}] has requested password reset email.`);
            setResetPasswordResponse(user);
            setRetryTimer(30);
        } catch (error: any) {
            // handle error here
            const msg = 'Something went wrong during password reset request.';
            logToServer(
                'info',
                `User [${data.username}] has trouble requesting password reset email. ${error?.message}`
            );
            setError('server', { type: 'custom', message: msg });
            toastError({ body: msg, title: 'Reset password failure', persist: true });
        }
    };

    useEffect(() => {
        if (resetPasswordResponse) {
            const timer = setTimeout(() => setRetryTimer(retryTimer - 1), 1000);
            if (retryTimer < 1) {
                clearTimeout(timer);
                setResetPasswordResponse(undefined);
            }
        }
    }, [resetPasswordResponse, retryTimer]);

    return (
        <div className="h-full flex flex-col justify-center py-12 px-6 bg-brand-background">
            <LoginLogo />
            <div className="absolute top-6 right-6">
                <LanguageButton className="w-4 h-4" />
            </div>
            {resetPasswordResponse ? (
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                    <div className="p-6 sm:p-8 rounded-md sm:rounded-lg sm:px-10 bg-brand-background-dark border-black brand-shadow">
                        <div className="text-lg text-brand-neutral-200">
                            <p>{language[lang].forgotPassword.successMessage}</p>
                            <p>{language[lang].forgotPassword.pleaseCheckEmail}</p>
                        </div>
                        <p className="text-sm text-brand-neutral-400">
                            {language[lang].forgotPassword.didntReceiveEmail} {language[lang].forgotPassword.resendIn}{' '}
                            {retryTimer}
                        </p>
                    </div>
                </div>
            ) : (
                <FormProvider {...form}>
                    <form className="relative" onSubmit={handleSubmit(onSubmit)}>
                        <div className="relative my-6 mx-auto w-full max-w-md">
                            <div className="p-4 sm:p-6 rounded-md sm:rounded-lg sm:px-8 bg-brand-background brand-shadow z-0">
                                <div className="absolute top-4 right-4">
                                    <StatusIndicator showLabel={false} size="md" />
                                </div>
                                <div className="flex flex-col gap-5">
                                    <div className="flex gap-1 items-center justify-center text-center text-sm mb-4">
                                        <p className="text-brand-neutral-200">{language[lang].forgotPassword.title}</p>
                                    </div>
                                    <RHFInput
                                        {...register('username')}
                                        label={language[lang].forgotPassword.username}
                                        autoComplete="username"
                                    />
                                    {errors['server'] && isSubmitted ? (
                                        <span className="text-brand-red text-xs">{errors['server'].message}</span>
                                    ) : null}
                                    <Button isLoading={isSubmitting}>
                                        {language[lang].forgotPassword.resetButton}
                                    </Button>
                                </div>
                                <div
                                    className="mt-6 relative flex justify-center text-sm font-medium text-brand-neutral-300 hover:text-brand-text cursor-pointer"
                                    onClick={() => navigate('/login')}>
                                    {language[lang].forgotPassword.backToLogin}
                                </div>
                            </div>
                        </div>
                        <LoginFooter />
                    </form>
                </FormProvider>
            )}
        </div>
    );
}

export default ForgotPassword;
